import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { api, errText, btnDark } from "./api";

export function LastImportBanner({ li }) {
  if (!li) return null;
  const reasons = [
    li.already_in_crm ? `${li.already_in_crm} already in CRM` : "",
    li.suppressed ? `${li.suppressed} do-not-contact` : "",
    li.duplicate_in_file ? `${li.duplicate_in_file} duplicate in file` : "",
    li.invalid ? `${li.invalid} invalid email` : "",
  ].filter(Boolean);
  const skipped = li.skipped ?? ((li.invalid || 0) + (li.duplicates || 0) + (li.suppressed || 0));
  return (
    <div className="mt-4 border-l-2 border-[#A09680] bg-[#FAF7F1] px-4 py-2.5 text-[12.5px] text-[#0F1825] flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="last-import-banner">
      <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680] mr-1">Last upload</span>
      <span><strong data-testid="last-import-imported">{li.imported}</strong> imported</span>
      <span>· <strong data-testid="last-import-skipped">{skipped}</strong> skipped{reasons.length ? ` (${reasons.join(", ")})` : ""}</span>
      <span>· <strong className={li.previously_contacted ? "text-[#B7791F]" : ""} data-testid="last-import-previously">{li.previously_contacted || 0}</strong> previously emailed — will <strong>NOT</strong> be emailed again</span>
      <span className="ml-auto font-mono text-[10px] text-[#A09680]">{li.file ? `${li.file} · ` : ""}{li.at ? new Date(li.at).toLocaleString() : ""}</span>
    </div>
  );
}

export function ImportCard({ onImported, lastImport }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // 0..100 | null
  const [phase, setPhase] = useState("");          // "uploading" | "parsing" | ""
  const [summary, setSummary] = useState(null);
  const [fileName, setFileName] = useState("");

  const onFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setSummary(null);
    setProgress(0);
    setPhase("uploading");
    try {
      const res = await api.importFile(file, (ev) => {
        if (ev.total) {
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setProgress(pct);
          if (pct >= 100) setPhase("parsing");
        }
      });
      setSummary(res);
      toast.success(`${res.imported} contact${res.imported === 1 ? "" : "s"} imported`);
      onImported();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setProgress(null);
      setPhase("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="bg-white border border-[#E8E1D2] p-5" data-testid="import-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">1 · Import contacts</div>
          <p className="mt-1 text-[12.5px] text-[#5C6470] leading-snug">
            CSV or Excel — every sheet is read, any column layout. We pick up name, designation, company and email, skip invalid emails and duplicates, and flag anyone you emailed in a past campaign.
          </p>
        </div>
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file-input" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null} className={btnDark} data-testid="import-button">
          {progress !== null ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload CSV / XLSX
        </button>
      </div>

      {progress !== null && (
        <div className="mt-4" data-testid="import-progress">
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] mb-1.5">
            <span className="inline-flex items-center gap-2"><FileSpreadsheet size={12} /> {fileName}</span>
            <span>{phase === "parsing" ? "Parsing sheets & de-duplicating…" : `Uploading · ${progress}%`}</span>
          </div>
          <div className="h-1.5 w-full bg-[#F0EBDC] overflow-hidden">
            <div className={`h-full bg-[#C84C21] transition-[width] duration-300 ${phase === "parsing" ? "animate-pulse" : ""}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {summary && (
        <div className="mt-4 border-t border-[#F0EBDC] pt-4" data-testid="import-summary">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
            <span><span className="font-serif text-[1.3rem] text-[#2E4A38] font-medium mr-1.5" data-testid="import-imported">{summary.imported}</span> imported</span>
            <span><span className="font-serif text-[1.3rem] text-[#C84C21] font-medium mr-1.5" data-testid="import-invalid">{summary.invalid}</span> invalid emails</span>
            <span><span className="font-serif text-[1.3rem] text-[#5C6470] font-medium mr-1.5" data-testid="import-duplicates">{summary.duplicates}</span> duplicates skipped{summary.already_in_crm || summary.duplicate_in_file ? <span className="text-[11.5px] text-[#A09680]"> ({summary.already_in_crm || 0} already in CRM · {summary.duplicate_in_file || 0} repeated in file)</span> : null}</span>
            {summary.suppressed > 0 && <span><span className="font-serif text-[1.3rem] text-[#5C6470] font-medium mr-1.5">{summary.suppressed}</span> on do-not-contact list</span>}
            {summary.previously_contacted > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[#B7791F]" data-testid="import-previously-contacted">
                <AlertTriangle size={13} /> <span className="font-serif text-[1.3rem] font-medium">{summary.previously_contacted}</span> previously contacted (imported with a warning badge)
              </span>
            )}
            <span className="text-[#A09680] font-mono text-[10.5px] self-center">{summary.rows} rows · sheets: {(summary.sheets || []).join(", ")}</span>
          </div>
          {summary.invalid_samples?.length > 0 && (
            <ul className="mt-2 text-[11.5px] text-[#5C6470] font-mono space-y-0.5">
              {summary.invalid_samples.map((s) => <li key={s}>· {s}</li>)}
            </ul>
          )}
        </div>
      )}
      <LastImportBanner li={lastImport} />
    </div>
  );
}
